import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { JobDetailCardComponent, JobDetailCardData } from '../job-detail-card/job-detail-card.component';
import { FilterSidebarComponent } from '../filter-sidebar/filter-sidebar.component';
import { RecommendedJob } from '../../models/job.model';
import { JobService } from '../../services/job.service';
import { FilterStateService } from '../../services/filter-state.service';
import { LanguageRoutingService } from '../../services/language-routing.service';
import { finalize, Subject, takeUntil, debounceTime } from 'rxjs';
import { JobHeroComponent } from './job-hero/job-hero.component';
import { ActiveFiltersComponent } from './active-filters/active-filters.component';
import { ErrorComponent } from '../shared/error/error.component';
import { SkeletonLoaderComponent } from '../shared/skeleton-loader/skeleton-loader.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ToastService } from '../../services/toast.service';
import { getErrorTranslationKey } from '../../../../../../libs/shared/src/lib/i18n/error-translations';
import { AuthService } from '../../services/auth.service';

interface ActiveFilter {
  category: string;
  value: string;
}

@Component({
  selector: 'app-job-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    JobDetailCardComponent,
    FilterSidebarComponent,
    JobHeroComponent,
    ActiveFiltersComponent,
    SkeletonLoaderComponent,
    ErrorComponent,
    TranslocoModule,
  ],
  templateUrl: './job-list.component.html',
  styleUrls: ['./job-list.component.scss'],
})
export class JobListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  jobs: RecommendedJob[] = [];
  mappedJobs: JobDetailCardData[] = [];
  loading = true;
  error = false;
  currentPage = 1;
  pageSize = 10;
  totalPages = 5;
  totalJobs = 0;
  sortBy = 'newest';
  isModalOpen = false;
  sortDropdownOpen = false;
  pageSizeDropdownOpen = false;
  mobileFiltersOpen = false;
  activeFiltersOpen = false;
  activeFilters: ActiveFilter[] = [];
  pageSizeOptions = [5, 10, 20, 50, 100];
  sortOptions = [
    { value: 'newest', label: 'jobs.sort.newest' },
    { value: 'salary_desc', label: 'jobs.sort.salaryDesc' },
    { value: 'salary_asc', label: 'jobs.sort.salaryAsc' },
    { value: 'title_asc', label: 'jobs.sort.titleAsc' },
    { value: 'title_desc', label: 'jobs.sort.titleDesc' },
  ];

  // Advanced filter properties
  salaryRange = { min: 0, max: 0 };
  selectedSalaryPeriod = 'month';

  // Salary periods from API
  salaryPeriods: { id: number; periodKey: string; sortOrder: number }[] = [];

  // Employment types from API
  employmentTypes: { id: number; name: string; key: string; checked: boolean; count: number }[] = [];

  // Education levels from API
  educationLevels: { id: number; name: string; key: string; checked: boolean; count: number }[] = [];

  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  // Dismissal of the AI recommendations banner. Persisted in localStorage
  // so it stays hidden between page reloads on the same device.
  private static readonly AI_CTA_DISMISS_KEY = 'sameta:aiCta:dismissed';
  aiCtaDismissed = false;

  dismissAiCta(): void {
    this.aiCtaDismissed = true;
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(JobListComponent.AI_CTA_DISMISS_KEY, '1');
      } catch {
        // localStorage may be blocked (private mode); the in-memory flag is enough.
      }
    }
  }

  private readAiCtaDismissed(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      this.aiCtaDismissed = localStorage.getItem(JobListComponent.AI_CTA_DISMISS_KEY) === '1';
    } catch {
      this.aiCtaDismissed = false;
    }
  }

  constructor(
    private readonly jobService: JobService,
    private readonly filterStateService: FilterStateService,
    private readonly languageRoutingService: LanguageRoutingService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastService: ToastService,
    private readonly translocoService: TranslocoService,
    private readonly authService: AuthService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  ngOnInit(): void {
    this.readAiCtaDismissed();
    // First, load filters from URL query params if present
    this.loadFiltersFromUrl();

    // Subscribe to filter data to populate modal options
    this.filterStateService.filtersData$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      // Populate salary periods
      this.salaryPeriods = data.salaryPeriods.sort((a, b) => a.sortOrder - b.sortOrder);

      // Populate employment types
      this.employmentTypes = data.employmentTypes.map((emp) => ({
        id: emp.id,
        name: emp.employmentTypeKey,
        key: emp.employmentTypeKey,
        checked: false,
        count: emp.count,
      }));

      // Populate education levels
      this.educationLevels = data.educationLevels.map((edu) => ({
        id: edu.id,
        name: edu.educationKey,
        key: edu.educationKey,
        checked: false,
        count: edu.count,
      }));

      // Sync checked state with current filter state
      this.syncModalWithFilterState();

      // Sync active filters display (important for page refresh)
      this.syncActiveFilters();
    });

    // Subscribe to filter state changes to sync modal
    this.filterStateService.filterState$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300) // Debounce to avoid too many API calls
      )
      .subscribe((filterState) => {
        this.currentPage = 1; // Reset to first page on filter change
        this.syncFiltersToUrl();
        this.loadJobs();
        this.syncModalWithFilterState();
        this.syncActiveFilters();
      });

    // Close dropdowns when clicking outside
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('click', this.closeDropdowns.bind(this));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Remove event listener
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('click', this.closeDropdowns.bind(this));
    }
  }

  /**
   * Load jobs from the service using filter state
   */
  loadJobs(): void {
    this.loading = true;
    this.error = false;

    // Calculate offset based on current page and page size
    const offset = (this.currentPage - 1) * this.pageSize;

    // Get current filter state
    const filterState = this.filterStateService.getCurrentFilterState();

    this.jobService
      .getJobs(
        this.pageSize,
        offset,
        filterState.searchKeyword,
        filterState.searchLocation,
        filterState.selectedCategoryIDs,
        filterState.selectedPositionIDs,
        filterState.selectedEducationLevelIDs,
        filterState.selectedEmploymentTypeIDs,
        filterState.selectedCompanyIDs,
        filterState.salaryFilter,
        this.sortBy,
        filterState.searchLatitude,
        filterState.searchLongitude,
        filterState.searchRadius
      )
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.jobs = response.jobs;
          this.mappedJobs = this.mapJobsToCardData(response.jobs);
          this.totalJobs = response.total;
          this.totalPages = Math.ceil(response.total / this.pageSize);
        },
        error: (err) => {
          console.error('Error loading jobs:', err);
          this.error = true;
        },
      });
  }

  // Filter methods
  toggleFilter(category: string, value: string): void {
    const index = this.activeFilters.findIndex((filter) => filter.category === category && filter.value === value);

    if (index > -1) {
      this.activeFilters.splice(index, 1);
    } else {
      this.activeFilters.push({ category, value });
    }
  }

  removeFilter(filter: ActiveFilter): void {
    // Clear the corresponding filter in filter state service
    if (filter.category === 'jobs.filter') {
      this.filterStateService.setSearchKeyword('');
    } else if (filter.category === 'job.location') {
      this.filterStateService.setSearchLocation('');
    } else if (filter.category === 'filters.workAreas') {
      const filtersData = this.filterStateService.getFiltersData();
      // Extract the key from the translation key (e.g., "jobCategories.software_development" -> "software_development")
      const categoryKey = filter.value.replace('jobCategories.', '');
      const category = filtersData.categories.find((c) => c.categoryKey === categoryKey);
      if (category) {
        this.filterStateService.toggleCategory(category.id);
      }
    } else if (filter.category === 'filters.professions') {
      const filtersData = this.filterStateService.getFiltersData();
      const positionKey = filter.value.replace('jobPositions.', '');
      const position = filtersData.positions.find((p) => p.positionKey === positionKey);
      if (position) {
        this.filterStateService.togglePosition(position.id);
      }
    } else if (filter.category === 'filters.requiredEducation') {
      const filtersData = this.filterStateService.getFiltersData();
      const educationKey = filter.value.replace('jobEducationLevels.', '');
      const education = filtersData.educationLevels.find((e) => e.educationKey === educationKey);
      if (education) {
        this.filterStateService.toggleEducationLevel(education.id);
      }
    } else if (filter.category === 'filters.employmentType') {
      const filtersData = this.filterStateService.getFiltersData();
      const employmentKey = filter.value.replace('jobEmploymentTypes.', '');
      const employment = filtersData.employmentTypes.find((e) => e.employmentTypeKey === employmentKey);
      if (employment) {
        this.filterStateService.toggleEmploymentType(employment.id);
      }
    } else if (filter.category === 'filters.salary') {
      this.filterStateService.setSalaryFilter(0, 0, 'month');
    }
  }

  clearAllFilters(): void {
    this.activeFilters = [];
    this.currentPage = 1;
    this.sortBy = 'newest';
    this.filterStateService.clearAllFilters();
    this.syncFiltersToUrl();
  }

  // Sync active filters display with filter state
  private syncActiveFilters(): void {
    this.activeFilters = [];
    const filterState = this.filterStateService.getCurrentFilterState();
    const filtersData = this.filterStateService.getFiltersData();

    // Add search keyword filter
    if (filterState.searchKeyword) {
      this.activeFilters.push({ category: 'jobs.filter', value: filterState.searchKeyword });
    }

    // Add search location filter
    if (filterState.searchLocation) {
      this.activeFilters.push({ category: 'job.location', value: filterState.searchLocation });
    }

    // Add category filters
    filterState.selectedCategoryIDs.forEach((id) => {
      const category = filtersData.categories.find((c) => c.id === id);
      if (category) {
        this.activeFilters.push({
          category: 'filters.workAreas',
          value: `jobCategories.${category.categoryKey}`,
        });
      }
    });

    // Add position filters
    filterState.selectedPositionIDs.forEach((id) => {
      const position = filtersData.positions.find((p) => p.id === id);
      if (position) {
        this.activeFilters.push({
          category: 'filters.professions',
          value: `jobPositions.${position.positionKey}`,
        });
      }
    });

    // Add education level filters
    filterState.selectedEducationLevelIDs.forEach((id) => {
      const education = filtersData.educationLevels.find((e) => e.id === id);
      if (education) {
        this.activeFilters.push({
          category: 'filters.requiredEducation',
          value: `jobEducationLevels.${education.educationKey}`,
        });
      }
    });

    // Add employment type filters
    filterState.selectedEmploymentTypeIDs.forEach((id) => {
      const employmentType = filtersData.employmentTypes.find((e) => e.id === id);
      if (employmentType) {
        this.activeFilters.push({
          category: 'filters.employmentType',
          value: `jobEmploymentTypes.${employmentType.employmentTypeKey}`,
        });
      }
    });

    // Add salary filter
    if (filterState.salaryFilter.minSalary > 0 || filterState.salaryFilter.maxSalary > 0) {
      // Create a structured salary value that can be translated in the template
      const periodKey = `jobSalaryPeriods.${filterState.salaryFilter.periodKey}`;
      let salaryValue = '';

      if (filterState.salaryFilter.minSalary > 0 && filterState.salaryFilter.maxSalary > 0) {
        salaryValue = `${filterState.salaryFilter.minSalary} - ${filterState.salaryFilter.maxSalary} €|${periodKey}`;
      } else if (filterState.salaryFilter.minSalary > 0) {
        salaryValue = `From ${filterState.salaryFilter.minSalary} €|${periodKey}`;
      } else {
        salaryValue = `Up to ${filterState.salaryFilter.maxSalary} €|${periodKey}`;
      }

      this.activeFilters.push({ category: 'filters.salary', value: salaryValue });
    }
  }

  // Pagination methods
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.syncFiltersToUrl();
      this.loadJobs();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.syncFiltersToUrl();
      this.loadJobs();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.syncFiltersToUrl();
      this.loadJobs();
    }
  }

  firstPage(): void {
    this.goToPage(1);
  }

  lastPage(): void {
    this.goToPage(this.totalPages);
  }

  /**
   * Generate array of page numbers to display in pagination
   * Returns: Array of page numbers and 'ellipsis' strings
   * Example: [1, 2, 3, 'ellipsis', 10] or [1, 'ellipsis', 5, 6, 7, 'ellipsis', 10]
   */
  getVisiblePages(): (number | string)[] {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages: (number | string)[] = [];
    const current = this.currentPage;
    const total = this.totalPages;

    // Check if mobile (only in browser)
    const isMobile = isPlatformBrowser(this.platformId) && window.innerWidth < 768;

    // Mobile: show fewer pages (threshold = 5)
    // Desktop: show more pages (threshold = 7)
    const showAllThreshold = isMobile ? 5 : 7;

    // Always show first page
    pages.push(1);

    if (total <= showAllThreshold) {
      // Show all pages if within threshold
      for (let i = 2; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Complex logic for many pages
      if (isMobile) {
        // Mobile: More compact - show 1 ... current ... last
        if (current <= 2) {
          // Near start: 1 2 3 ... last
          pages.push(2);
          if (total > 3) {
            pages.push(3);
            pages.push('ellipsis');
          }
          pages.push(total);
        } else if (current >= total - 1) {
          // Near end: 1 ... (total-2) (total-1) total
          pages.push('ellipsis');
          if (total > 3) {
            pages.push(total - 2);
          }
          pages.push(total - 1);
          if (total > 1) {
            pages.push(total);
          }
        } else {
          // Middle: 1 ... current ... last
          pages.push('ellipsis');
          pages.push(current);
          pages.push('ellipsis');
          pages.push(total);
        }
      } else {
        // Desktop: Show more context
        if (current <= 3) {
          // Near start: show 1 2 3 4 ... last
          for (let i = 2; i <= 4; i++) {
            pages.push(i);
          }
          pages.push('ellipsis');
          pages.push(total);
        } else if (current >= total - 2) {
          // Near end: show 1 ... (total-3) (total-2) (total-1) total
          pages.push('ellipsis');
          for (let i = total - 3; i <= total; i++) {
            pages.push(i);
          }
        } else {
          // In middle: show 1 ... (current-1) current (current+1) ... last
          pages.push('ellipsis');
          pages.push(current - 1);
          pages.push(current);
          pages.push(current + 1);
          pages.push('ellipsis');
          pages.push(total);
        }
      }
    }

    return pages;
  }

  setPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1; // Reset to first page when changing page size
    this.pageSizeDropdownOpen = false;
    this.syncFiltersToUrl();
    this.loadJobs();
  }

  // Sorting methods
  setSortBy(sort: string): void {
    this.sortBy = sort;
    this.sortDropdownOpen = false;
    this.syncFiltersToUrl();
    this.loadJobs();
  }

  // Get label for current sort option
  getSortLabel(value: string): string {
    const option = this.sortOptions.find((opt) => opt.value === value);
    return option ? option.label : 'jobs.sort.newest';
  }

  // Close all dropdowns
  closeDropdowns(event?: MouseEvent): void {
    // Check if click is on a dropdown button or inside dropdown menu
    if (event) {
      const target = event.target as HTMLElement;
      const isDropdownButton = target.closest('.dropdown__btn');
      const isDropdownMenu = target.closest('.dropdown__menu');

      // Don't close if clicking on dropdown button or menu
      if (isDropdownButton || isDropdownMenu) {
        return;
      }
    }

    this.sortDropdownOpen = false;
    this.pageSizeDropdownOpen = false;
  }

  // Handle search from hero component
  onSearch(searchData: { keyword: string; location: string }): void {
    // Update filter state service - syncActiveFilters() will be called automatically via filterState$ subscription
    this.filterStateService.setSearchKeyword(searchData.keyword);
    // Note: location lat/lng/radius are already set by job-hero component's onLocationSelected handler
  }

  // Modal methods
  openModal(): void {
    this.isModalOpen = true;
    // Only manipulate DOM if we're in the browser
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'hidden';
    }
  }

  closeModal(): void {
    this.isModalOpen = false;
    // Only manipulate DOM if we're in the browser
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
    }
  }

  toggleMobileFilters(): void {
    this.mobileFiltersOpen = !this.mobileFiltersOpen;
  }

  toggleActiveFilters(): void {
    this.activeFiltersOpen = !this.activeFiltersOpen;
  }

  // Prevent modal close when clicking inside modal content
  stopPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }

  // Salary range methods
  updateSalaryRange(min: number, max: number): void {
    this.salaryRange = { min, max };
  }

  setSalaryPeriod(period: string): void {
    this.selectedSalaryPeriod = period;
  }

  // Map Jobs to JobDetailCardData
  private mapJobsToCardData(jobs: RecommendedJob[]): JobDetailCardData[] {
    // JobDetailCardData extends RecommendedJob, so we can use them directly
    return jobs.map((job) => {
      return {
        ...job,
        location: job.placeOfWork,
        isSaved: job.isSaved || false,
      } as JobDetailCardData;
    });
  }

  // Handle save/unsave actions
  onSaveJob(jobId: string): void {
    const jobIndex = this.mappedJobs.findIndex((j) => String(j.id) === jobId);
    if (jobIndex === -1) return;

    this.jobService.saveJob(jobId).subscribe({
      next: (response) => {
        if (response.status === 200) {
          this.mappedJobs[jobIndex].isSaved = true;
        }
      },
      error: (error) => {
        if (error?.error?.code === 'UNAUTHORIZED_ACCESS') {
          const titleKey = getErrorTranslationKey('SAVE_JOB_AUTH_REQUIRED', 'title', 'jobOperations');
          const messageKey = getErrorTranslationKey('SAVE_JOB_AUTH_REQUIRED', 'message', 'jobOperations');
          this.toastService.error(
            this.translocoService.translate(titleKey),
            this.translocoService.translate(messageKey),
            true,
            5000
          );
        } else {
          const titleKey = getErrorTranslationKey('SAVE_JOB_FAILED', 'title', 'jobOperations');
          const messageKey = getErrorTranslationKey('SAVE_JOB_FAILED', 'message', 'jobOperations');
          this.toastService.error(
            this.translocoService.translate(titleKey),
            this.translocoService.translate(messageKey),
            true,
            5000
          );
        }
      },
    });
  }

  onUnsaveJob(jobId: string): void {
    // Find the job
    const jobIndex = this.mappedJobs.findIndex((j) => String(j.id) === jobId);
    if (jobIndex === -1) return;

    // Call the service to unsave the job
    this.jobService.unsaveJob(jobId).subscribe({
      next: (response) => {
        if (response.status === 200) {
          // Update the job's saved status
          this.mappedJobs[jobIndex].isSaved = false;
        }
      },
      error: (error) => {
        console.error('Error unsaving job:', error);
        // Optionally show an error message to the user
      },
    });
  }

  // Get localized route
  getLocalizedRoute(path: string): string {
    return this.languageRoutingService.getLocalizedRoute(path);
  }

  // Apply advanced filters
  applyAdvancedFilters(): void {
    // Apply salary filter
    this.filterStateService.setSalaryFilter(this.salaryRange.min, this.salaryRange.max, this.selectedSalaryPeriod);

    // Apply employment type filters
    this.employmentTypes.forEach((empType) => {
      const currentState = this.filterStateService.getCurrentFilterState();
      const isSelected = currentState.selectedEmploymentTypeIDs.includes(empType.id);

      // If checked but not selected, toggle it on
      if (empType.checked && !isSelected) {
        this.filterStateService.toggleEmploymentType(empType.id);
      }
      // If not checked but selected, toggle it off
      else if (!empType.checked && isSelected) {
        this.filterStateService.toggleEmploymentType(empType.id);
      }
    });

    // Apply education level filters
    this.educationLevels.forEach((eduLevel) => {
      const currentState = this.filterStateService.getCurrentFilterState();
      const isSelected = currentState.selectedEducationLevelIDs.includes(eduLevel.id);

      // If checked but not selected, toggle it on
      if (eduLevel.checked && !isSelected) {
        this.filterStateService.toggleEducationLevel(eduLevel.id);
      }
      // If not checked but selected, toggle it off
      else if (!eduLevel.checked && isSelected) {
        this.filterStateService.toggleEducationLevel(eduLevel.id);
      }
    });

    // Close the modal
    this.closeModal();
  }

  // Check if there are any active filters (including all filters, not just modal ones)
  hasActiveFiltersInModal(): boolean {
    const filterState = this.filterStateService.getCurrentFilterState();

    // Check if salary is set
    const hasSalary = filterState.salaryFilter.minSalary > 0 || filterState.salaryFilter.maxSalary > 0;

    // Check if any employment type is selected
    const hasEmploymentType = filterState.selectedEmploymentTypeIDs.length > 0;

    // Check if any education level is selected
    const hasEducationLevel = filterState.selectedEducationLevelIDs.length > 0;

    // Check if any category is selected
    const hasCategory = filterState.selectedCategoryIDs.length > 0;

    // Check if any position is selected
    const hasPosition = filterState.selectedPositionIDs.length > 0;

    // Check if search filters are set
    const hasSearch =
      !!(filterState.searchKeyword && filterState.searchKeyword.length > 0) || !!(filterState.searchLocation && filterState.searchLocation.length > 0);

    return hasSalary || hasEmploymentType || hasEducationLevel || hasCategory || hasPosition || hasSearch;
  }

  // Sync modal state with current filter state
  private syncModalWithFilterState(): void {
    const filterState = this.filterStateService.getCurrentFilterState();

    // Sync salary
    this.salaryRange.min = filterState.salaryFilter.minSalary;
    this.salaryRange.max = filterState.salaryFilter.maxSalary;
    this.selectedSalaryPeriod = filterState.salaryFilter.periodKey;

    // Sync employment types
    this.employmentTypes.forEach((empType) => {
      empType.checked = filterState.selectedEmploymentTypeIDs.includes(empType.id);
    });

    // Sync education levels
    this.educationLevels.forEach((eduLevel) => {
      eduLevel.checked = filterState.selectedEducationLevelIDs.includes(eduLevel.id);
    });
  }

  // Load filters from URL query parameters
  private loadFiltersFromUrl(): void {
    const params = this.route.snapshot.queryParams;

    // Clear all existing filters first to ensure URL is the source of truth
    // This prevents toggle issues when navigating back with the same filters
    this.filterStateService.clearAllFilters();

    // Load page, page size, and sort
    if (params['page']) {
      this.currentPage = parseInt(params['page'], 10);
    }
    if (params['pageSize']) {
      this.pageSize = parseInt(params['pageSize'], 10);
    }
    if (params['sortBy']) {
      this.sortBy = params['sortBy'];
    }

    // Load category IDs
    if (params['categoryIDs']) {
      const categoryIDs = Array.isArray(params['categoryIDs'])
        ? params['categoryIDs'].map((id: string) => parseInt(id, 10))
        : [parseInt(params['categoryIDs'], 10)];
      categoryIDs.forEach((id) => this.filterStateService.toggleCategory(id));
    }

    // Load position IDs
    if (params['positionIDs']) {
      const positionIDs = Array.isArray(params['positionIDs'])
        ? params['positionIDs'].map((id: string) => parseInt(id, 10))
        : [parseInt(params['positionIDs'], 10)];
      positionIDs.forEach((id) => this.filterStateService.togglePosition(id));
    }

    // Load education level IDs
    if (params['educationLevelIDs']) {
      const educationLevelIDs = Array.isArray(params['educationLevelIDs'])
        ? params['educationLevelIDs'].map((id: string) => parseInt(id, 10))
        : [parseInt(params['educationLevelIDs'], 10)];
      educationLevelIDs.forEach((id) => this.filterStateService.toggleEducationLevel(id));
    }

    // Load employment type IDs
    if (params['employmentTypeIDs']) {
      const employmentTypeIDs = Array.isArray(params['employmentTypeIDs'])
        ? params['employmentTypeIDs'].map((id: string) => parseInt(id, 10))
        : [parseInt(params['employmentTypeIDs'], 10)];
      employmentTypeIDs.forEach((id) => this.filterStateService.toggleEmploymentType(id));
    }

    // Load company IDs
    if (params['companyIDs']) {
      const companyIDs = Array.isArray(params['companyIDs'])
        ? params['companyIDs'].map((id: string) => parseInt(id, 10))
        : [parseInt(params['companyIDs'], 10)];
      companyIDs.forEach((id) => this.filterStateService.toggleCompany(id));
    }

    // Load salary filter
    if (params['minSalary'] || params['maxSalary'] || params['salaryPeriod']) {
      const minSalary = params['minSalary'] ? parseInt(params['minSalary'], 10) : 0;
      const maxSalary = params['maxSalary'] ? parseInt(params['maxSalary'], 10) : 0;
      const periodKey = params['salaryPeriod'] || 'month';
      this.filterStateService.setSalaryFilter(minSalary, maxSalary, periodKey);
    }

    // Load search filters
    if (params['filter']) {
      this.filterStateService.setSearchKeyword(params['filter']);
    }
    if (params['filterLocation']) {
      const latitude = params['searchLatitude'] ? parseFloat(params['searchLatitude']) : null;
      const longitude = params['searchLongitude'] ? parseFloat(params['searchLongitude']) : null;
      const radius = params['searchRadius'] ? parseFloat(params['searchRadius']) : null;
      this.filterStateService.setSearchLocation(params['filterLocation'], latitude, longitude, radius);
    }
  }

  // Sync current filter state to URL
  private syncFiltersToUrl(): void {
    const filterState = this.filterStateService.getCurrentFilterState();
    const queryParams: any = {};

    // Only add page if not default (1)
    if (this.currentPage !== 1) {
      queryParams.page = this.currentPage;
    }

    // Only add pageSize if not default (10)
    if (this.pageSize !== 10) {
      queryParams.pageSize = this.pageSize;
    }

    // Only add sortBy if not default (newest)
    if (this.sortBy !== 'newest') {
      queryParams.sortBy = this.sortBy;
    }

    // Add filter parameters only if they have values
    if (filterState.selectedCategoryIDs.length > 0) {
      queryParams.categoryIDs = filterState.selectedCategoryIDs;
    }
    if (filterState.selectedPositionIDs.length > 0) {
      queryParams.positionIDs = filterState.selectedPositionIDs;
    }
    if (filterState.selectedEducationLevelIDs.length > 0) {
      queryParams.educationLevelIDs = filterState.selectedEducationLevelIDs;
    }
    if (filterState.selectedEmploymentTypeIDs.length > 0) {
      queryParams.employmentTypeIDs = filterState.selectedEmploymentTypeIDs;
    }
    if (filterState.selectedCompanyIDs.length > 0) {
      queryParams.companyIDs = filterState.selectedCompanyIDs;
    }
    if (filterState.salaryFilter.minSalary > 0) {
      queryParams.minSalary = filterState.salaryFilter.minSalary;
    }
    if (filterState.salaryFilter.maxSalary > 0) {
      queryParams.maxSalary = filterState.salaryFilter.maxSalary;
    }
    if (filterState.salaryFilter.periodKey && (filterState.salaryFilter.minSalary > 0 || filterState.salaryFilter.maxSalary > 0)) {
      queryParams.salaryPeriod = filterState.salaryFilter.periodKey;
    }
    if (filterState.searchKeyword) {
      queryParams.filter = filterState.searchKeyword;
    }
    if (filterState.searchLocation) {
      queryParams.filterLocation = filterState.searchLocation;
    }
    if (filterState.searchLatitude !== null) {
      queryParams.searchLatitude = filterState.searchLatitude;
    }
    if (filterState.searchLongitude !== null) {
      queryParams.searchLongitude = filterState.searchLongitude;
    }
    if (filterState.searchRadius !== null) {
      queryParams.searchRadius = filterState.searchRadius;
    }

    // Update URL without navigation - use empty object to clear all params if nothing to set
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }
}
